/**
 * Interfaces for table select options
 */

/**
 * Represents a single option in a select column
 */
export interface SelectOption {
  /** Unique identifier for the option */
  id: string;

  /** Display text for the option */
  label: string;

  /** Color for the option tag */
  color: string;
}

/**
 * Represents the options configuration for a select column
 */
export interface SelectOptionsConfig {
  /** Array of select options */
  options: SelectOption[];
}

/**
 * Creates a new SelectOption with generated ID
 *
 * @param label The display text for the option
 * @returns A new SelectOption with a unique ID
 */
// List of available colors for select options
export const SELECT_OPTION_COLORS = [
  'blue',
  'purple',
  'pink',
  'red',
  'orange',
  'yellow',
  'green',
  'cyan',
  'indigo',
  'teal',
];

/**
 * Generates a deterministic color based on the input string
 *
 * @param input The string to generate a color for
 * @returns A color from the SELECT_OPTION_COLORS array
 */
export function getColorForString(input: string): string {
  // Simple hash function to convert string to a number
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }

  // Use the absolute value of the hash to select a color
  const index = Math.abs(hash) % SELECT_OPTION_COLORS.length;
  return SELECT_OPTION_COLORS[index];
}

/**
 * Creates a new SelectOption with generated ID and color
 *
 * @param label The display text for the option
 * @returns A new SelectOption with a unique ID and color
 */
export function createSelectOption(label: string): SelectOption {
  // For now, use the label as ID for new options to maintain compatibility with migration
  // Later, we could switch to using UUIDs
  const id = label;

  // Generate a deterministic color based on the label
  const color = getColorForString(label);

  return {
    id,
    label,
    color,
  };
}

/**
 * Finds a select option by ID in an array of options
 *
 * @param options Array of SelectOption objects
 * @param id The ID to look for
 * @returns The matching SelectOption or undefined if not found
 */
export function findOptionById(options: SelectOption[], id: string): SelectOption | undefined {
  return options.find(option => option.id === id);
}

/**
 * Gets the display label for an option ID
 *
 * @param options Array of SelectOption objects
 * @param id The ID to look up
 * @returns The label for the ID, or the ID itself if not found
 */
export function getLabelForId(options: SelectOption[], id: string): string {
  const option = findOptionById(options, id);
  return option ? option.label : id;
}

/**
 * Converts from the legacy format (string array) to the new format (SelectOption array)
 *
 * @param legacyOptions Array of option strings or undefined
 * @returns Array of SelectOption objects
 */
export function convertLegacyOptions(legacyOptions?: string[]): SelectOption[] {
  if (!legacyOptions || !Array.isArray(legacyOptions)) {
    return [];
  }

  return legacyOptions.map(option => createSelectOption(option));
}

/**
 * Updates an option's label in the options array
 *
 * @param options Array of SelectOption objects
 * @param id ID of the option to update
 * @param newLabel New label for the option
 * @returns New array with the updated option
 */
export function updateOptionLabel(
  options: SelectOption[],
  id: string,
  newLabel: string
): SelectOption[] {
  return options.map(option => (option.id === id ? { ...option, label: newLabel } : option));
}

/**
 * Updates an option's color in the options array
 *
 * @param options Array of SelectOption objects
 * @param id ID of the option to update
 * @param newColor New color for the option
 * @returns New array with the updated option
 */
export function updateOptionColor(
  options: SelectOption[],
  id: string,
  newColor: string
): SelectOption[] {
  return options.map(option => (option.id === id ? { ...option, color: newColor } : option));
}
